import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserType } from '../enums/userType';

export interface IUser {
  id: number;
  name: string;
  email: string;
  avatarUrl?: string;
  userType?: UserType;
}

const defaultPath = '/';


@Injectable()
export class AuthService {
  private userSubject = new BehaviorSubject<IUser | null>(null);

  private _user?: IUser | null;

  get userType(): UserType | undefined | null {
    if (!this.loggedIn) return null
    return this._user?.userType;
  }

  get loggedIn(): boolean {
    return !!sessionStorage.getItem('token');
  }

  private _lastAuthenticatedPath: string = defaultPath;
  set lastAuthenticatedPath(value: string) {
    this._lastAuthenticatedPath = value;
  }

  constructor(private router: Router,
    private http: HttpClient
  ) { }

  async logIn(email: string, password: string) {

    try {
      const formData = new FormData();

      // Text verileri ekleme
      formData.append('email', email);
      formData.append('password', password);

      const resultToken: any = await firstValueFrom(this.http.post(`${environment.apiUrl}/Auth`, formData))
      sessionStorage.setItem('token', resultToken.accessToken)

      await this.loadUserInfo()

      this.router.navigate([this._lastAuthenticatedPath]);

      return {
        isOk: true,
        data: this._user
      };
    }
    catch {
      return {
        isOk: false,
        message: "Authentication failed"
      };
    }
  }

  async loadUserInfo() {
    this._user = (await firstValueFrom(this.http.get(`${environment.apiUrl}/Auth`))) as IUser
    this.userSubject.next(this._user);
  }

  subscribeUser() {
    return this.userSubject.asObservable();
  }

  async createAccount(email: string, password: string) {
    try {
      // Send request

      this.router.navigate(['/create-account']);
      return {
        isOk: true
      };
    }
    catch {
      return {
        isOk: false,
        message: "Failed to create account"
      };
    }
  }

  async changePassword(password: string, recoveryCode: string) {
    try {
      const formData = new FormData();
      formData.append('guid', recoveryCode);
      formData.append('password', password);
      await firstValueFrom(this.http.post(`${environment.apiUrl}/Auth/ChangePassword`, formData))

      return {
        isOk: true
      };
    }
    catch {
      return {
        isOk: false,
        message: "Şifre değiştirme hatası."
      }
    }
  }

  async resetPassword(email: string) {
    try {
      const formData = new FormData();
      formData.append('email', email);
      const resultToken: any = await firstValueFrom(this.http.post(`${environment.apiUrl}/Auth/ResetPassword`, formData))

      return {
        isOk: true
      };
    }
    catch {
      return {
        isOk: false,
        message: "Şifre sıfırlama hatası"
      };
    }
  }

  async logOut() {
    this._user = null;
    sessionStorage.removeItem("token")
    this.router.navigate(['/login-form']);
  }
}

@Injectable()
export class AuthGuardService implements CanActivate {
  constructor(private router: Router, private authService: AuthService) { }

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const isLoggedIn = this.authService.loggedIn;

    // Eğer kullanıcı giriş yapmamışsa, login sayfasına yönlendirin
    if (!isLoggedIn) {
      this.router.navigate(['/login-form']);
      return false;
    }

    // Kullanıcı bilgilerini yükleyin
    if (!this.authService.userType) {
      await this.authService.loadUserInfo();
    }

    // Rol kontrolü yapın
    if (this.authService.userType && this.authService.userType != UserType.Admin) {
      if (!route.data['roles'] ||
        !route.data['roles'][0] ||
        !route.data['roles'].includes(UserType[this.authService.userType])) {
        this.router.navigate(['/home']);
        return false;
      }
    }

    // Son olarak rota yüklensin
    return true;
  }
}

 